// The words used for a marketOverview() result, so the page and (later) the
// MCP server describe it the same way. Only these claims are ever made:
// seen most often · highest average position · top 3 share of ad appearances ·
// shown with a price · appeared in X of Y searches · first seen on [date].
// Always "searches", never "scans".

import { jobTotalUnits, plannedSampleSpan } from "./jobs";

// Every channel's name, used everywhere: page tabs, tiles, table headings,
// toggles and fact wording (Scott, 14 Oct 2026: long but descriptive, so
// there's no questioning it).
export const CHANNEL_NAMES = {
  lsa: "Local Services Ads",
  ppc: "Google Ads",
  shopping: "Google Shopping",
  local: "Google Maps",
  ai: "AEO · AI answers",
  seo: "Google SEO",
  bing: "Bing",
};

// The hover text wherever a channel's name shows (only AEO has one so far).
export const CHANNEL_TOOLTIPS = {
  ai: "Are you mentioned when people ask ChatGPT or Google's AI?",
};

// The channels visibility counts: "Google Ads, Google Maps or Google SEO".
export const VISIBILITY_CHANNELS = `${CHANNEL_NAMES.ppc}, ${CHANNEL_NAMES.local} or ${CHANNEL_NAMES.seo}`;
export const VISIBILITY_DESCRIPTION = `% of searches seen in ${VISIBILITY_CHANNELS}`;

// What the position fact calls each channel: "Highest average Google Ads position".
const POSITION_NAMES = CHANNEL_NAMES;

const SPACING = {
  minute: "every minute",
  "15min": "every 15 minutes",
  hour: "every hour",
  day: "every day",
  minutely: "every minute",
  hourly: "every hour",
  daily: "every day",
};

export const STATUS_LABELS = {
  complete: "Complete",
  in_progress: "In progress",
  stopped: "Stopped early",
  error: "Error",
};

const UK_DATE = { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" };
const UK_TIME = { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Europe/London" };

function validDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

// "2026-10-13T10:00:00Z" -> "13 Oct 2026" (UK time)
export function formatDate(iso) {
  const date = validDate(iso);
  return date ? new Intl.DateTimeFormat("en-GB", UK_DATE).format(date) : "";
}

// "2026-10-13T10:15:00Z" -> "11:15" (UK time)
export function formatTime(iso) {
  const date = validDate(iso);
  return date ? new Intl.DateTimeFormat("en-GB", UK_TIME).format(date) : "";
}

function plural(n, word, many = `${word}s`) {
  return `${n} ${n === 1 ? word : many}`;
}

// " so far" while the job is still running.
export function soFar(result) {
  return result.job.status === "in_progress" ? " so far" : "";
}

// The Multi-channel tile's share: "(15%)" of advertisers seen, whole percent;
// "" when there are no advertisers.
export function multiChannelShare(totals) {
  if (!(totals.advertisers > 0)) return "";
  return `(${Math.round((totals.multiChannel / totals.advertisers) * 100)}%)`;
}

// The note next to the searches count when it's estimated (coverage.basis
// "inferred": jobs that ran before each search's outcome was recorded).
// { label, title } or null.
export function coverageNote(result) {
  if (result.coverage.basis !== "inferred") return null;
  return {
    label: "estimated",
    title: "Estimated: this job ran before AdAudit recorded each search's outcome, so which searches failed is worked out from the results.",
  };
}

// When the data provider (Oxylabs) refused the scan (job.status "error"):
// { label: "Data provider error" }, otherwise null. Say this, never "no data"
// or "no ads", so a broken login is never shown as an empty market.
export function providerError(result) {
  if (result.job.status !== "error") return null;
  return { label: "Data provider error" };
}

// The snapshot title under the job name (Scott, 3 Nov 2026: every scan is a
// Snapshot for now, never an Audit):
//   "Market Snapshot · 120 searches · 2 hours"
//   "Advertiser Snapshot · 40 of 120 searches · 2 hours"  (while running)
// kind: "market" | "advertiser". The time is first sample to last, left out
// for one sample or an old job with no planned times.
export function snapshotTitle(result, kind) {
  const { coverage, sampling } = result;
  const parts = [kind === "advertiser" ? "Advertiser Snapshot" : "Market Snapshot"];
  const planned = plural(coverage.searchesPlanned, "search", "searches");
  parts.push(result.job.status === "in_progress" ? `${coverage.searchesCompleted} of ${planned}` : planned);
  const span = spanText(sampling.firstSampleAt, sampling.lastSampleAt);
  if (span) parts.push(span);
  return parts.join(" · ");
}

// The same title for a row in the Audits Snapshots list, from the job row
// alone (no searches loaded): "Market Snapshot · 120 searches · 2 hours", or
// "Market Snapshot · 40 of 120 searches · 2 hours" while running.
export function snapshotListTitle(job) {
  const planned = plural(jobTotalUnits(job), "search", "searches");
  const parts = ["Market Snapshot", job.status === "running" ? `${job.completed_units || 0} of ${planned}` : planned];
  const { first, last } = plannedSampleSpan(job);
  const span = spanText(first, last);
  if (span) parts.push(span);
  return parts.join(" · ");
}

// First sample to last, rounded: "12 min", "1 hour", "3 hours", "1 day", "2 days".
function spanText(first, last) {
  const from = validDate(first);
  const to = validDate(last);
  if (!from || !to) return "";
  const minutes = Math.round((to.getTime() - from.getTime()) / 60000);
  if (minutes < 1) return "";
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) return plural(Math.round(minutes / 60), "hour");
  return plural(Math.round(minutes / (24 * 60)), "day");
}

// The report bar's second line, in three parts so the page can bold the middle:
//   plan:     "4 keywords × 5 postcodes × 3 samples (every hour) × mobile + desktop · 10:15–12:20"
//   searches: "118 of 120 searches"  (or "... so far", "Stopped early: ...", "Data provider error: ...")
//   note:     "(2 failed, not counted)" or ""
export function samplingParts(result) {
  const { sampling, coverage, gapSummary, grid } = result;
  const postcodes = new Set(grid.map(g => g.postcode)).size;
  const sizes = [plural(gapSummary.keywordsBySearches.length, "keyword"), plural(postcodes, "postcode")];
  if (sampling.samplesPerRun) {
    const spacing = SPACING[sampling.spacing];
    sizes.push(plural(sampling.samplesPerRun, "sample") + (spacing && sampling.samplesPerRun > 1 ? ` (${spacing})` : ""));
  }
  if (sampling.devices.length > 0) sizes.push(sampling.devices.join(" + "));

  const plan = [sizes.join(" × ")];
  const when = timeRange(sampling.firstSampleAt, sampling.lastSampleAt);
  if (when) plan.push(when);

  let searches = `${coverage.searchesCompleted} of ${plural(coverage.searchesPlanned, "search", "searches")}`;
  if (result.job.status === "in_progress") searches += " so far";
  if (result.job.status === "stopped") searches = `Stopped early: ${searches}`;
  const error = providerError(result);
  if (error) searches = `${error.label}: ${searches}`;
  const note = coverage.searchesFailed > 0 ? `(${coverage.searchesFailed} failed, not counted)` : "";

  return { plan: plan.join(" · "), searches, note };
}

// The same line as one string (for the MCP). It still starts "One-off audit"
// until the MCP's own names pass; the app no longer shows those words.
export function samplingLine(result) {
  const { plan, searches, note } = samplingParts(result);
  return `One-off audit · ${plan} · ${searches}${note ? ` ${note}` : ""}`;
}

// "10:15–12:20" on one day, otherwise "13 Oct 2026 – 14 Oct 2026".
function timeRange(first, last) {
  if (!validDate(first)) return "";
  if (!validDate(last) || first === last) return formatTime(first);
  if (formatDate(first) === formatDate(last)) return `${formatTime(first)}–${formatTime(last)}`;
  return `${formatDate(first)} – ${formatDate(last)}`;
}

// One "Observed" chip: { label, value } - the page shows the value in bold.
export function factParts(fact) {
  switch (fact.claim) {
    case "seen_most_often":
      return { label: "Seen most often", value: `${fact.advertiser} · ${fact.value} of ${fact.of} searches` };
    case "highest_average_position":
      return { label: `Highest average ${POSITION_NAMES[fact.channel] || fact.channel} position`, value: `${fact.advertiser} · ${fact.value}` };
    case "top_3_ad_share":
      return { label: "Top 3 hold", value: `${Math.round(fact.value * 100)}% of ad appearances` };
    case "shown_with_a_price":
      return { label: "Shown with a price", value: `${fact.advertiser} · ${fact.value} of ${fact.of} searches` };
    case "no_ads_seen":
      return { label: "No ads seen in", value: `${fact.cells} of ${fact.of} keyword × postcode` };
    case "first_seen":
      return { label: "First seen", value: `${fact.advertiser} on ${formatDate(fact.at)}` };
    default:
      return { label: "", value: "" };
  }
}

export function factText(fact) {
  const { label, value } = factParts(fact);
  return `${label} ${value}`.trim();
}
